import { renderBlockIcon, renderItemIcon } from './Hotbar.js';
import { MAX_STACK_SIZE } from '../constants.js';

const SLOT_SIZE = 44;
const GAP = 2;
const COLS = 9;

/**
 * ChestScreen — Minecraft-style chest UI.
 * Shows 27 or 54 chest slots + 27 player storage + 9 hotbar.
 * Supports single chests (27 slots) and double chests (54 slots).
 * Uses inventory.getUnifiedSlot/setUnifiedSlot for player slots.
 */
export class ChestScreen {
  constructor(inventory) {
    this.inventory = inventory;
    this.isOpen = false;
    this.chestData = null;   // { x, y, z, slots: Array(27|54), isDouble?: boolean }
    this.heldItem = null;
    this._onClose = null;
    this._chestSlotCount = 27;

    this._build();
  }

  _build() {
    this.overlay = document.createElement('div');
    this.overlay.id = 'chest-overlay';
    this.overlay.style.cssText = `
      position: fixed; top: 0; left: 0; right: 0; bottom: 0;
      background: rgba(0,0,0,0.55); z-index: 500;
      display: none; justify-content: center; align-items: center;
      cursor: default;
    `;

    this.panel = document.createElement('div');
    this.panel.style.cssText = `
      background: #C6C6C6;
      border: 4px solid #2A2A2A;
      border-top-color: #FFFFFF; border-left-color: #FFFFFF;
      padding: 8px;
      font-family: 'Courier New', monospace; color: #404040;
      user-select: none; image-rendering: pixelated;
      box-shadow: 4px 4px 0 rgba(0,0,0,0.3), inset 0 0 0 2px #8B8B8B;
    `;

    // Title
    this.titleEl = document.createElement('div');
    this.titleEl.textContent = 'Cofre';
    this.titleEl.style.cssText = 'font-size: 14px; margin-bottom: 6px; color: #404040;';
    this.panel.appendChild(this.titleEl);

    // Chest slots container (will be rebuilt on open for 27 or 54 slots)
    this.chestGrid = document.createElement('div');
    this.chestGrid.style.cssText = `
      display: grid;
      grid-template-columns: repeat(${COLS}, ${SLOT_SIZE}px);
      gap: ${GAP}px;
      margin-bottom: 10px;
    `;
    this.chestSlots = [];
    this.panel.appendChild(this.chestGrid);

    // Separator
    this.chestSep = document.createElement('div');
    this.chestSep.style.cssText = 'height: 2px; background: #8B8B8B; margin: 6px 0;';
    this.panel.appendChild(this.chestSep);

    // Player inventory label
    const invLabel = document.createElement('div');
    invLabel.textContent = 'Inventario';
    invLabel.style.cssText = 'font-size: 12px; margin-bottom: 4px; color: #606060;';
    this.panel.appendChild(invLabel);

    // Player storage (27 slots: unified indices 9-35)
    this.playerGrid = document.createElement('div');
    this.playerGrid.style.cssText = `
      display: grid;
      grid-template-columns: repeat(${COLS}, ${SLOT_SIZE}px);
      gap: ${GAP}px;
      margin-bottom: 6px;
    `;
    this.playerSlots = [];
    for (let i = 0; i < 27; i++) {
      const slot = this._createSlot();
      slot.addEventListener('click', () => this._onPlayerSlotClick(i + 9));
      slot.addEventListener('contextmenu', (e) => { e.preventDefault(); this._onPlayerSlotRightClick(i + 9); });
      this.playerGrid.appendChild(slot);
      this.playerSlots.push(slot);
    }
    this.panel.appendChild(this.playerGrid);

    // Player hotbar (9 slots: unified indices 0-8)
    const hotbarSep = document.createElement('div');
    hotbarSep.style.cssText = 'height: 2px; background: #8B8B8B; margin: 4px 0;';
    this.panel.appendChild(hotbarSep);

    this.hotbarGrid = document.createElement('div');
    this.hotbarGrid.style.cssText = `
      display: grid;
      grid-template-columns: repeat(${COLS}, ${SLOT_SIZE}px);
      gap: ${GAP}px;
    `;
    this.hotbarSlots = [];
    for (let i = 0; i < 9; i++) {
      const slot = this._createSlot();
      slot.addEventListener('click', () => this._onPlayerSlotClick(i));
      slot.addEventListener('contextmenu', (e) => { e.preventDefault(); this._onPlayerSlotRightClick(i); });
      this.hotbarGrid.appendChild(slot);
      this.hotbarSlots.push(slot);
    }
    this.panel.appendChild(this.hotbarGrid);

    // Held item cursor
    this.cursorEl = document.createElement('div');
    this.cursorEl.style.cssText = `
      position: fixed; pointer-events: none; z-index: 600;
      width: ${SLOT_SIZE}px; height: ${SLOT_SIZE}px;
      display: none;
    `;
    document.body.appendChild(this.cursorEl);

    this.overlay.appendChild(this.panel);
    document.body.appendChild(this.overlay);

    // Track mouse for held item
    this._onMouseMove = (e) => {
      if (!this.isOpen) return;
      this.cursorEl.style.left = (e.clientX - SLOT_SIZE / 2) + 'px';
      this.cursorEl.style.top = (e.clientY - SLOT_SIZE / 2) + 'px';
    };
    // mousemove listener added in open(), removed in close()
  }

  /**
   * Rebuild the chest grid slots based on the slot count (27 or 54).
   */
  _buildChestGrid(slotCount) {
    this.chestGrid.innerHTML = '';
    this.chestSlots = [];
    this._chestSlotCount = slotCount;
    for (let i = 0; i < slotCount; i++) {
      const slot = this._createSlot();
      const idx = i;
      slot.addEventListener('click', () => this._onChestSlotClick(idx));
      slot.addEventListener('contextmenu', (e) => { e.preventDefault(); this._onChestSlotRightClick(idx); });
      this.chestGrid.appendChild(slot);
      this.chestSlots.push(slot);
    }
  }

  _createSlot() {
    const slot = document.createElement('div');
    slot.style.cssText = `
      width: ${SLOT_SIZE}px; height: ${SLOT_SIZE}px;
      background: #8B8B8B;
      border: 2px solid #373737;
      border-top-color: #FFFFFF; border-left-color: #FFFFFF;
      position: relative; cursor: pointer;
      display: flex; align-items: center; justify-content: center;
    `;
    return slot;
  }

  // Check if two items match (same blockId or same itemId)
  _itemsMatch(a, b) {
    if (!a || !b) return false;
    if (a.blockId !== undefined && b.blockId !== undefined) return a.blockId === b.blockId;
    if (a.itemId !== undefined && b.itemId !== undefined) return a.itemId === b.itemId;
    return false;
  }

  _isEmpty(item) {
    return !item || (!item.count) || (item.blockId === 0 && item.itemId === undefined);
  }

  open(chestData, onClose) {
    this.chestData = chestData;
    this._onClose = onClose;
    this.heldItem = null;
    this.cursorEl.style.display = 'none';

    // Determine slot count (27 for single, 54 for double)
    const slotCount = chestData.slots.length;
    this._buildChestGrid(slotCount);

    // Set title based on chest type
    this.titleEl.textContent = (slotCount >= 54) ? 'Cofre Doble' : 'Cofre';

    this.isOpen = true;
    this.overlay.style.display = 'flex';
    document.addEventListener('mousemove', this._onMouseMove);
    this._render();
  }

  close() {
    if (this.heldItem) {
      // Return held item to player inventory
      if (this.heldItem.blockId !== undefined) {
        this.inventory.addBlock(this.heldItem.blockId);
      } else if (this.heldItem.itemId !== undefined) {
        this.inventory.addItem(this.heldItem.itemId, this.heldItem.count);
      }
      this.heldItem = null;
    }
    this.isOpen = false;
    this.overlay.style.display = 'none';
    this.cursorEl.style.display = 'none';
    document.removeEventListener('mousemove', this._onMouseMove);
    if (this._onClose) this._onClose(this.chestData);
  }

  _onChestSlotClick(idx) {
    const slots = this.chestData.slots;
    if (this.heldItem) {
      if (this._isEmpty(slots[idx])) {
        slots[idx] = { ...this.heldItem };
        this.heldItem = null;
      } else if (this._itemsMatch(slots[idx], this.heldItem)) {
        const space = MAX_STACK_SIZE - slots[idx].count;
        const transfer = Math.min(space, this.heldItem.count);
        slots[idx].count += transfer;
        this.heldItem.count -= transfer;
        if (this.heldItem.count <= 0) this.heldItem = null;
      } else {
        const tmp = slots[idx];
        slots[idx] = { ...this.heldItem };
        this.heldItem = tmp;
      }
    } else if (!this._isEmpty(slots[idx])) {
      this.heldItem = { ...slots[idx] };
      slots[idx] = null;
    }
    this._render();
  }

  _onChestSlotRightClick(idx) {
    const slots = this.chestData.slots;
    if (this.heldItem) {
      if (this._isEmpty(slots[idx])) {
        slots[idx] = { ...this.heldItem, count: 1 };
        this.heldItem.count--;
        if (this.heldItem.count <= 0) this.heldItem = null;
      } else if (this._itemsMatch(slots[idx], this.heldItem) && slots[idx].count < MAX_STACK_SIZE) {
        slots[idx].count++;
        this.heldItem.count--;
        if (this.heldItem.count <= 0) this.heldItem = null;
      }
    } else if (!this._isEmpty(slots[idx])) {
      const half = Math.ceil(slots[idx].count / 2);
      this.heldItem = { ...slots[idx], count: half };
      slots[idx].count -= half;
      if (slots[idx].count <= 0) slots[idx] = null;
    }
    this._render();
  }

  _onPlayerSlotClick(idx) {
    const current = this.inventory.getUnifiedSlot(idx);
    if (this.heldItem) {
      if (this._isEmpty(current)) {
        this.inventory.setUnifiedSlot(idx, { ...this.heldItem });
        this.heldItem = null;
      } else if (this._itemsMatch(current, this.heldItem)) {
        const space = MAX_STACK_SIZE - current.count;
        const transfer = Math.min(space, this.heldItem.count);
        current.count += transfer;
        this.heldItem.count -= transfer;
        if (this.heldItem.count <= 0) this.heldItem = null;
      } else {
        const tmp = { ...current };
        this.inventory.setUnifiedSlot(idx, { ...this.heldItem });
        this.heldItem = tmp;
      }
    } else if (!this._isEmpty(current)) {
      this.heldItem = { ...current };
      this.inventory.setUnifiedSlot(idx, null);
    }
    this._render();
  }

  _onPlayerSlotRightClick(idx) {
    const current = this.inventory.getUnifiedSlot(idx);
    if (this.heldItem) {
      if (this._isEmpty(current)) {
        this.inventory.setUnifiedSlot(idx, { ...this.heldItem, count: 1 });
        this.heldItem.count--;
        if (this.heldItem.count <= 0) this.heldItem = null;
      } else if (this._itemsMatch(current, this.heldItem) && current.count < MAX_STACK_SIZE) {
        current.count++;
        this.heldItem.count--;
        if (this.heldItem.count <= 0) this.heldItem = null;
      }
    } else if (!this._isEmpty(current)) {
      const half = Math.ceil(current.count / 2);
      this.heldItem = { ...current, count: half };
      current.count -= half;
      if (current.count <= 0) this.inventory.setUnifiedSlot(idx, null);
    }
    this._render();
  }

  _render() {
    // Render chest slots (27 or 54)
    for (let i = 0; i < this._chestSlotCount; i++) {
      this._renderSlot(this.chestSlots[i], this.chestData.slots[i]);
    }
    // Render player storage (unified indices 9-35)
    for (let i = 0; i < 27; i++) {
      this._renderSlot(this.playerSlots[i], this.inventory.getUnifiedSlot(i + 9));
    }
    // Render hotbar (unified indices 0-8)
    for (let i = 0; i < 9; i++) {
      this._renderSlot(this.hotbarSlots[i], this.inventory.getUnifiedSlot(i));
    }
    // Render held item on cursor
    if (this.heldItem) {
      this.cursorEl.style.display = 'block';
      this.cursorEl.innerHTML = '';
      this._renderSlot(this.cursorEl, this.heldItem);
      this.cursorEl.style.background = 'transparent';
      this.cursorEl.style.border = 'none';
    } else {
      this.cursorEl.style.display = 'none';
    }
  }

  _renderSlot(slotEl, item) {
    slotEl.innerHTML = '';
    if (this._isEmpty(item)) return;

    // Render icon
    if (item.itemId !== undefined) {
      slotEl.innerHTML = renderItemIcon(item.itemId, 32);
    } else {
      slotEl.innerHTML = renderBlockIcon(item.blockId, 32);
    }

    if (item.count > 1) {
      const countEl = document.createElement('div');
      countEl.textContent = item.count;
      countEl.style.cssText = `
        position: absolute; bottom: 1px; right: 3px;
        font-size: 11px; font-weight: bold; color: white;
        text-shadow: 1px 1px 0 #000;
        pointer-events: none;
      `;
      slotEl.appendChild(countEl);
    }
  }
}
